import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, AlertTriangle, CheckCircle, XCircle, Undo2, TruckIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PaginationControls } from '@/components/ui/pagination-controls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParkedInventoryManagement = () => {
  const [activeParked, setActiveParked] = useState([]);
  const [resolvedParked, setResolvedParked] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [activePagination, setActivePagination] = useState({
    page: 1, pageSize: 50, totalCount: 0, totalPages: 0
  });
  const [resolvedPagination, setResolvedPagination] = useState({
    page: 1, pageSize: 50, totalCount: 0, totalPages: 0
  });

  // Park Dialog
  const [showParkDialog, setShowParkDialog] = useState(false);
  const [parkData, setParkData] = useState({
    warehouse_id: '',
    item_id: '',
    qty: '',
    status: 'REWORK',
    reason: '',
    vendor_id: '',
    grn_id: '',
    notes: ''
  });

  // Action Dialog
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(''); // 'rework' or 'return'
  const [selectedParked, setSelectedParked] = useState(null);
  const [actionNotes, setActionNotes] = useState('');

  // Segregation Dialog
  const [showSegregateDialog, setShowSegregateDialog] = useState(false);
  const [segregateData, setSegregateData] = useState({
    rework_qty: 0,
    rejected_qty: 0,
    notes: ''
  });

  useEffect(() => {
    fetchMasterData();
    fetchActiveParked();
    fetchResolvedParked();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [invRes, itemsRes, vendorsRes, warehousesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/items`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/warehouses`),
        axios.get(`${API}/parked-inventory/summary`)
      ]);
      setInventory(invRes.data);
      setItems(itemsRes.data);
      setVendors(vendorsRes.data);
      setWarehouses(warehousesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchActiveParked = async (page = activePagination.page) => {
    try {
      const params = new URLSearchParams({
        status: 'active',
        page: page.toString(),
        page_size: activePagination.pageSize.toString()
      });
      const response = await axios.get(`${API}/parked-inventory?${params.toString()}`);
      
      if (response.data.data) {
        setActiveParked(response.data.data);
        setActivePagination(prev => ({
          ...prev,
          page: response.data.pagination.page,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setActiveParked(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching active parked:', error);
    }
  };

  const fetchResolvedParked = async (page = resolvedPagination.page) => {
    try {
      const params = new URLSearchParams({
        status: 'resolved',
        page: page.toString(),
        page_size: resolvedPagination.pageSize.toString()
      });
      const response = await axios.get(`${API}/parked-inventory?${params.toString()}`);
      
      if (response.data.data) {
        setResolvedParked(response.data.data);
        setResolvedPagination(prev => ({
          ...prev,
          page: response.data.pagination.page,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setResolvedParked(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching resolved parked:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivePageChange = (newPage) => {
    setActivePagination(prev => ({ ...prev, page: newPage }));
    fetchActiveParked(newPage);
  };

  const handleResolvedPageChange = (newPage) => {
    setResolvedPagination(prev => ({ ...prev, page: newPage }));
    fetchResolvedParked(newPage);
  };

  const refreshData = () => {
    fetchMasterData();
    fetchActiveParked(1);
    fetchResolvedParked(1);
  };

  const handleParkInventory = async () => {
    if (!parkData.warehouse_id || !parkData.item_id || !parkData.qty || !parkData.status) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API}/parked-inventory/park`, parkData);
      toast.success(`Inventory parked successfully as ${parkData.status}`);
      setShowParkDialog(false);
      setParkData({
        warehouse_id: '',
        item_id: '',
        qty: '',
        status: 'REWORK',
        reason: '',
        vendor_id: '',
        grn_id: '',
        notes: ''
      });
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to park inventory');
    }
  };

  const handleReworkComplete = async () => {
    try {
      console.log('Completing rework for:', selectedParked);
      await axios.post(
        `${API}/parked-inventory/${selectedParked.id}/rework-complete`,
        null,
        { params: { notes: actionNotes } }
      );
      toast.success('Rework completed, inventory restored to available');
      setShowActionDialog(false);
      setActionNotes('');
      setSelectedParked(null);
      refreshData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to complete rework';
      console.error('Rework completion error:', error.response?.data || error);
      toast.error(errorMsg);
      // Close dialog on error as well to prevent confusion
      setShowActionDialog(false);
      setActionNotes('');
      setSelectedParked(null);
      refreshData(); // Refresh to show current state
    }
  };

  const handleReturnToVendor = async () => {
    try {
      await axios.post(
        `${API}/parked-inventory/${selectedParked.id}/return-to-vendor`,
        null,
        { params: { notes: actionNotes } }
      );
      toast.success('Rejected inventory returned to vendor');
      setShowActionDialog(false);
      setActionNotes('');
      setSelectedParked(null);
      refreshData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to return to vendor';
      toast.error(errorMsg);
      console.error('Return to vendor error:', error);
      // Close dialog on error as well
      setShowActionDialog(false);
      setActionNotes('');
      setSelectedParked(null);
      refreshData(); // Refresh to show current state
    }
  };

  const handleSegregateClick = (parked) => {
    setSelectedParked(parked);
    setSegregateData({
      rework_qty: parked.qty,
      rejected_qty: 0,
      notes: ''
    });
    setShowSegregateDialog(true);
  };

  const handleSegregateSubmit = async () => {
    if (!selectedParked) return;

    const total = segregateData.rework_qty + segregateData.rejected_qty;
    if (Math.abs(total - selectedParked.qty) > 0.01) {
      toast.error(`Rework + Rejected must equal total quantity (${selectedParked.qty})`);
      return;
    }

    try {
      await axios.post(`${API}/parked-inventory/${selectedParked.id}/segregate`, segregateData);
      toast.success(`Successfully segregated into ${segregateData.rework_qty > 0 ? 'REWORK' : ''}${segregateData.rework_qty > 0 && segregateData.rejected_qty > 0 ? ' and ' : ''}${segregateData.rejected_qty > 0 ? 'REJECTED' : ''}`);
      setShowSegregateDialog(false);
      setSegregateData({ rework_qty: 0, rejected_qty: 0, notes: '' });
      setSelectedParked(null);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to segregate inventory');
    }
  };

  const openActionDialog = (parked, type) => {
    console.log('Opening action dialog:', { parked: parked.id, type, status: parked.status });
    setSelectedParked(parked);
    setActionType(type);
    setShowActionDialog(true);
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || 'N/A';
  const getWarehouseName = (id) => warehouses.find(w => w.id === id)?.name || 'N/A';

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Parked</p>
                <p className="text-2xl font-bold">{summary.total_parked_qty || 0}</p>
              </div>
              <Package className="w-8 h-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Inspection</p>
                <p className="text-2xl font-bold text-blue-600">{summary.pending_qty || 0}</p>
                <p className="text-xs text-slate-400">{summary.pending_count || 0} items</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">For Rework</p>
                <p className="text-2xl font-bold text-orange-600">{summary.rework_qty || 0}</p>
                <p className="text-xs text-slate-400">{summary.rework_count || 0} items</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{summary.rejected_qty || 0}</p>
                <p className="text-xs text-slate-400">{summary.rejected_count || 0} items</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-center items-center h-full">
              <Button onClick={() => setShowParkDialog(true)} className="w-full">
                <Package className="w-4 h-4 mr-2" />
                Park Inventory
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Active Parked ({activeParked.length})</TabsTrigger>
          <TabsTrigger value="resolved">Resolved ({resolvedParked.length})</TabsTrigger>
        </TabsList>

        {/* Active Parked */}
        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>Active Parked Inventory</CardTitle>
              <CardDescription>Items awaiting rework completion or return to vendor</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : activeParked.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No active parked inventory</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parked Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Warehouse</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeParked.map((parked) => (
                      <TableRow key={parked.id}>
                        <TableCell>{parked.parked_date}</TableCell>
                        <TableCell className="text-sm">{getItemInfo(parked.item_id)}</TableCell>
                        <TableCell>{getWarehouseName(parked.warehouse_id)}</TableCell>
                        <TableCell className="font-semibold">{parked.qty}</TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              parked.status === 'PENDING_INSPECTION' ? 'bg-blue-100 text-blue-800' :
                              parked.status === 'REWORK' ? 'bg-orange-100 text-orange-800' : 
                              'bg-red-100 text-red-800'
                            }
                          >
                            {parked.status === 'PENDING_INSPECTION' ? 'PENDING INSPECTION' : parked.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{parked.reason || '-'}</TableCell>
                        <TableCell>{getVendorName(parked.vendor_id)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {parked.status === 'PENDING_INSPECTION' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSegregateClick(parked)}
                                className="text-purple-600 border-purple-600 hover:bg-purple-50"
                              >
                                <Package className="w-4 h-4 mr-1" />
                                Segregate
                              </Button>
                            )}
                            {parked.status === 'REWORK' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openActionDialog(parked, 'rework')}
                                className="text-green-600 border-green-600 hover:bg-green-50"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            )}
                            {parked.status === 'REJECTED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openActionDialog(parked, 'return')}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                              >
                                <TruckIcon className="w-4 h-4 mr-1" />
                                Return
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination Controls */}
              <PaginationControls pagination={activePagination} onPageChange={handleActivePageChange} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resolved Parked */}
        <TabsContent value="resolved">
          <Card>
            <CardHeader>
              <CardTitle>Resolved Parked Inventory</CardTitle>
              <CardDescription>History of reworked and returned items</CardDescription>
            </CardHeader>
            <CardContent>
              {resolvedParked.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No resolved items yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parked Date</TableHead>
                      <TableHead>Resolved Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Original Status</TableHead>
                      <TableHead>Resolution</TableHead>
                      <TableHead>Vendor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resolvedParked.map((parked) => (
                      <TableRow key={parked.id}>
                        <TableCell>{parked.parked_date}</TableCell>
                        <TableCell>{parked.resolved_date}</TableCell>
                        <TableCell className="text-sm">{getItemInfo(parked.item_id)}</TableCell>
                        <TableCell>{parked.qty}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{parked.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={parked.resolved_action === 'REWORKED' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}
                          >
                            {parked.resolved_action}
                          </Badge>
                        </TableCell>
                        <TableCell>{getVendorName(parked.vendor_id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination Controls */}
              <PaginationControls pagination={resolvedPagination} onPageChange={handleResolvedPageChange} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Park Inventory Dialog */}
      <Dialog open={showParkDialog} onOpenChange={setShowParkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Park Inventory</DialogTitle>
            <DialogDescription>
              Move inventory from available to parked state for rework or rejection
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Warehouse *</Label>
              <Select value={parkData.warehouse_id} onValueChange={(val) => setParkData({...parkData, warehouse_id: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item *</Label>
              <Select value={parkData.item_id} onValueChange={(val) => setParkData({...parkData, item_id: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.part_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity *</Label>
              <Input
                type="number"
                value={parkData.qty}
                onChange={(e) => setParkData({...parkData, qty: e.target.value})}
                placeholder="Enter quantity"
              />
            </div>
            <div>
              <Label>Status *</Label>
              <Select value={parkData.status} onValueChange={(val) => setParkData({...parkData, status: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REWORK">Rework Required</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select value={parkData.vendor_id} onValueChange={(val) => setParkData({...parkData, vendor_id: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={parkData.reason}
                onChange={(e) => setParkData({...parkData, reason: e.target.value})}
                placeholder="Quality issue, damage, etc."
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={parkData.notes}
                onChange={(e) => setParkData({...parkData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowParkDialog(false)}>Cancel</Button>
            <Button onClick={handleParkInventory}>Park Inventory</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog (Rework/Return) */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'rework' ? 'Complete Rework' : 'Return to Vendor'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'rework' 
                ? 'Mark rework as complete and restore inventory to available' 
                : 'Process return of rejected items to vendor'}
            </DialogDescription>
          </DialogHeader>
          {selectedParked && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Item:</span>
                  <span className="text-sm font-medium">{getItemInfo(selectedParked.item_id)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Quantity:</span>
                  <span className="text-sm font-medium">{selectedParked.qty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Vendor:</span>
                  <span className="text-sm font-medium">{getVendorName(selectedParked.vendor_id)}</span>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder="Additional notes for this action..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                console.log('Button clicked, actionType:', actionType);
                if (actionType === 'rework') {
                  handleReworkComplete();
                } else {
                  handleReturnToVendor();
                }
              }}
              className={actionType === 'rework' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              {actionType === 'rework' ? 'Complete Rework' : 'Return to Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Segregation Dialog */}
      <Dialog open={showSegregateDialog} onOpenChange={setShowSegregateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Segregate Parked Inventory</DialogTitle>
            <DialogDescription>
              Split this parked quantity into REWORK (can be fixed) and REJECTED (return to vendor)
            </DialogDescription>
          </DialogHeader>
          {selectedParked && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Item:</span>
                  <span className="text-sm font-medium">{getItemInfo(selectedParked.item_id)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Total Quantity:</span>
                  <span className="text-sm font-bold text-blue-600">{selectedParked.qty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Vendor:</span>
                  <span className="text-sm font-medium">{getVendorName(selectedParked.vendor_id)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rework_qty">REWORK Quantity</Label>
                  <Input
                    id="rework_qty"
                    type="number"
                    value={segregateData.rework_qty}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setSegregateData({
                        ...segregateData,
                        rework_qty: val,
                        rejected_qty: selectedParked.qty - val
                      });
                    }}
                    placeholder="0"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Can be fixed internally</p>
                </div>
                <div>
                  <Label htmlFor="rejected_qty">REJECTED Quantity</Label>
                  <Input
                    id="rejected_qty"
                    type="number"
                    value={segregateData.rejected_qty}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setSegregateData({
                        ...segregateData,
                        rejected_qty: val,
                        rework_qty: selectedParked.qty - val
                      });
                    }}
                    placeholder="0"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Must return to vendor</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-3 rounded-md">
                <p className="text-sm font-medium text-blue-900">Validation:</p>
                <p className="text-sm text-blue-700">
                  REWORK ({segregateData.rework_qty}) + REJECTED ({segregateData.rejected_qty}) = 
                  <span className={`font-semibold ml-1 ${Math.abs((segregateData.rework_qty + segregateData.rejected_qty) - selectedParked.qty) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                    {segregateData.rework_qty + segregateData.rejected_qty}
                  </span>
                  <span className="ml-1">(Must equal {selectedParked.qty})</span>
                </p>
              </div>

              <div>
                <Label htmlFor="segregate_notes">Notes (Optional)</Label>
                <Textarea
                  id="segregate_notes"
                  value={segregateData.notes}
                  onChange={(e) => setSegregateData({...segregateData, notes: e.target.value})}
                  placeholder="Reason for segregation, inspection findings..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                <div className="text-center">
                  <p className="text-xs text-slate-600 mb-1">Will create REWORK</p>
                  <p className="text-2xl font-bold text-orange-600">{segregateData.rework_qty}</p>
                  <p className="text-xs text-slate-500 mt-1">→ Fix & restore to inventory</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-600 mb-1">Will create REJECTED</p>
                  <p className="text-2xl font-bold text-red-600">{segregateData.rejected_qty}</p>
                  <p className="text-xs text-slate-500 mt-1">→ Return to vendor</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSegregateDialog(false)}>Cancel</Button>
            <Button onClick={handleSegregateSubmit} className="bg-purple-600 hover:bg-purple-700">
              <Package className="w-4 h-4 mr-2" />
              Segregate Inventory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParkedInventoryManagement;
